import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { useTheme } from '../../contexts/ThemeContext';
import Sidebar from '../../components/layout/Sidebar';
import Header from '../../components/layout/Header';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import { Label } from '../../components/ui/label';
import {
  Bell,
  CheckCircle2,
  Info,
  AlertTriangle,
  Zap,
  Trash2,
  CheckCheck,
  Plus,
  Loader2,
  ExternalLink,
  Clock
} from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter
} from '../../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue
} from '../../components/ui/select';
import { cn } from '../../lib/utils';
import api from '../../lib/axios';
import { toast } from 'sonner';

const NotificationsPage = () => {
  const { user, isAgency } = useAuth();
  const { theme } = useTheme();
  const [collapsed, setCollapsed] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [saving, setSaving] = useState(false);
  
  const [newNotif, setNewNotif] = useState({
    title: '',
    content: '',
    notification_type: 'info',
    link: '',
    target_role: ''
  });

  useEffect(() => {
    fetchNotifications();
  }, []);

  const fetchNotifications = async () => {
    try {
      const res = await api.get('/notifications/');
      setNotifications(res.data || []);
    } catch (error) {
      console.error('Error fetching notifications:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleMarkRead = async (id) => {
    try {
      await api.put(`/notifications/${id}/read`);
      setNotifications(notifications.map(n => 
        n.id === id ? { ...n, read: true } : n
      ));
    } catch (error) {
      toast.error('Erreur lors du marquage');
    }
  };

  const handleMarkAllRead = async () => {
    try {
      await api.put('/notifications/read-all');
      setNotifications(notifications.map(n => ({ ...n, read: true })));
      toast.success('Toutes les notifications marquées comme lues');
    } catch (error) {
      toast.error('Erreur lors du marquage');
    }
  };

  const handleDelete = async (id) => {
    try {
      await api.delete(`/notifications/${id}`);
      setNotifications(notifications.filter(n => n.id !== id));
      toast.success('Notification supprimée');
    } catch (error) {
      toast.error('Erreur lors de la suppression');
    }
  };

  const handleCreate = async () => {
    if (!newNotif.title || !newNotif.content) {
      toast.error('Veuillez remplir le titre et le contenu');
      return;
    }
    setSaving(true);
    try {
      await api.post('/notifications/', newNotif);
      toast.success('Notification créée');
      setShowCreateModal(false);
      setNewNotif({ title: '', content: '', notification_type: 'info', link: '', target_role: '' });
      fetchNotifications();
    } catch (error) {
      toast.error('Erreur lors de la création');
    } finally {
      setSaving(false);
    }
  };

  const getTypeIcon = (type) => {
    switch (type) {
      case 'success': return <CheckCircle2 className="w-5 h-5 text-green-500" />;
      case 'warning': return <AlertTriangle className="w-5 h-5 text-amber-500" />;
      case 'action': return <Zap className="w-5 h-5 text-purple-500" />;
      default: return <Info className="w-5 h-5 text-blue-500" />;
    }
  };

  const getTypeBadge = (type) => {
    const styles = {
      info: 'bg-blue-100 text-blue-700',
      success: 'bg-green-100 text-green-700',
      warning: 'bg-amber-100 text-amber-700',
      action: 'bg-purple-100 text-purple-700'
    };
    const labels = { info: 'Info', success: 'Succès', warning: 'Attention', action: 'Action' };
    return <Badge className={styles[type] || styles.info}>{labels[type] || type}</Badge>;
  };

  const formatTime = (dateStr) => {
    const date = new Date(dateStr);
    const now = new Date();
    const diff = now - date;
    if (diff < 3600000) return `Il y a ${Math.floor(diff / 60000)} min`;
    if (diff < 86400000) return `Il y a ${Math.floor(diff / 3600000)}h`;
    return date.toLocaleDateString('fr-FR', { day: 'numeric', month: 'short' });
  };

  const unreadCount = notifications.filter(n => !n.read).length;

  return (
    <div className="min-h-screen bg-[#f8fafc]">
      <Sidebar collapsed={collapsed} setCollapsed={setCollapsed} mobileOpen={mobileOpen} setMobileOpen={setMobileOpen} />
      <Header collapsed={collapsed} setMobileOpen={setMobileOpen} />
      
      <main className={cn("transition-all duration-300 px-6 md:px-8 pb-6 md:pb-8", collapsed ? "md:ml-20" : "md:ml-64")}
        style={{ paddingTop: "130px" }}
      >
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-8">
          <div>
            <h1 className="text-3xl font-bold flex items-center gap-3" style={{ color: theme.colors?.primary }}>
              <div className="w-10 h-10 rounded-xl flex items-center justify-center" style={{ backgroundColor: `${theme.colors?.accent}20` }}>
                <Bell className="w-5 h-5" style={{ color: theme.colors?.accent }} />
              </div>
              Notifications
            </h1>
            <p className="text-[#64748b] mt-1">
              {unreadCount > 0 ? `${unreadCount} non lue(s)` : 'Toutes lues'}
            </p>
          </div>
          
          <div className="flex gap-3">
            {unreadCount > 0 && (
              <Button variant="outline" onClick={handleMarkAllRead}>
                <CheckCheck className="w-4 h-4 mr-2" />
                Tout marquer comme lu
              </Button>
            )}
            {isAgency && (
              <Button onClick={() => setShowCreateModal(true)} style={{ backgroundColor: theme.colors?.accent }}>
                <Plus className="w-4 h-4 mr-2" />
                Nouvelle notification
              </Button>
            )}
          </div>
        </div>

        {loading ? (
          <div className="flex items-center justify-center h-64">
            <Loader2 className="w-8 h-8 animate-spin text-[#64748b]" />
          </div>
        ) : notifications.length === 0 ? (
          <Card className="card">
            <CardContent className="p-12 text-center">
              <Bell className="w-16 h-16 mx-auto mb-4 text-[#64748b] opacity-30" />
              <h3 className="text-lg font-semibold mb-2" style={{ color: theme.colors?.primary }}>Aucune notification</h3>
              <p className="text-[#64748b]">Vous n'avez pas encore de notifications</p>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-3">
            {notifications.map(notif => (
              <Card key={notif.id} className={cn("card transition-all", !notif.read && "border-l-4", !notif.read && "border-l-[var(--color-accent)]")}>
                <CardContent className="p-4">
                  <div className="flex items-start gap-4">
                    <div className="shrink-0 mt-1">{getTypeIcon(notif.notification_type)}</div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1">
                        <h3 className={cn("font-semibold", !notif.read ? "text-[#1a365d]" : "text-[#64748b]")}>{notif.title}</h3>
                        {getTypeBadge(notif.notification_type)}
                        {!notif.read && <Badge className="bg-[var(--color-accent)] text-white text-xs">Nouveau</Badge>}
                      </div>
                      <p className="text-sm text-[#64748b] mb-2">{notif.content}</p>
                      <div className="flex items-center gap-4 text-xs text-[#94a3b8]">
                        <span className="flex items-center gap-1"><Clock className="w-3 h-3" />{formatTime(notif.created_at)}</span>
                        {notif.link && (
                          <a href={notif.link} className="flex items-center gap-1 text-[var(--color-accent)] hover:underline">
                            <ExternalLink className="w-3 h-3" />Voir
                          </a>
                        )}
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      {!notif.read && (
                        <Button variant="ghost" size="sm" onClick={() => handleMarkRead(notif.id)}>
                          <CheckCircle2 className="w-4 h-4" />
                        </Button>
                      )}
                      {isAgency && (
                        <Button variant="ghost" size="sm" className="text-red-500" onClick={() => handleDelete(notif.id)}>
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      )}
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}

        <Dialog open={showCreateModal} onOpenChange={setShowCreateModal}>
          <DialogContent className="max-w-[95vw] sm:max-w-lg max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>Nouvelle notification</DialogTitle>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div>
                <Label>Titre *</Label>
                <Input value={newNotif.title} onChange={(e) => setNewNotif({ ...newNotif, title: e.target.value })} placeholder="Titre de la notification" className="mt-1" />
              </div>
              <div>
                <Label>Contenu *</Label>
                <Textarea value={newNotif.content} onChange={(e) => setNewNotif({ ...newNotif, content: e.target.value })} placeholder="Contenu de la notification" className="mt-1" />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Type</Label>
                  <Select value={newNotif.notification_type} onValueChange={(v) => setNewNotif({ ...newNotif, notification_type: v })}>
                    <SelectTrigger className="mt-1"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="info">Information</SelectItem>
                      <SelectItem value="success">Succès</SelectItem>
                      <SelectItem value="warning">Attention</SelectItem>
                      <SelectItem value="action">Action requise</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Destinataires</Label>
                  <Select value={newNotif.target_role || '_all'} onValueChange={(v) => setNewNotif({ ...newNotif, target_role: v === '_all' ? '' : v })}>
                    <SelectTrigger className="mt-1"><SelectValue placeholder="Tous" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="_all">Tous</SelectItem>
                      <SelectItem value="client">Clients</SelectItem>
                      <SelectItem value="team_member">Équipe</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div>
                <Label>Lien (optionnel)</Label>
                <Input value={newNotif.link} onChange={(e) => setNewNotif({ ...newNotif, link: e.target.value })} placeholder="/projects/123" className="mt-1" />
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowCreateModal(false)}>Annuler</Button>
              <Button onClick={handleCreate} disabled={saving} style={{ backgroundColor: theme.colors?.accent }}>
                {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Plus className="w-4 h-4 mr-2" />}
                Créer
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </main>
    </div>
  );
};

export default NotificationsPage;